import React from 'react';
import Table from "v4/components/ui/Table/Table";
import useMapImportForm
    from "v4/pages/admin/Import/components/ImportSteps/components/MapImport/components/MapImportFormTable/hooks/useMapImportForm";
import MapImportColumnFields
    from "v4/pages/admin/Import/components/ImportSteps/components/MapImport/components/MapImportColumnFields/MapImportColumnFields";
import SubmitButton from "v4/components/ui/SubmitButton/SubmitButton";
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import SearchContextPagination from "v4/components/utils/SearchContextPagination/SearchContextPagination";
import useImportTable from "v4/pages/admin/Import/components/ImportSteps/hooks/useImportTable";
import {IMPORT_MAPPING_DATA, IMPORT_MAPPING_DATA_SEARCH_FORM} from "v4/data/apiRoutes";
import ImportRowsInfos from "v4/pages/admin/Import/components/ImportSteps/components/ImportRowsInfos/ImportRowsInfos";
import FormRender from "v4/components/form/FormRender/FormRender";
import Confirm from "v4/components/ui/Confirm/Confirm";

export default function MapImportFormTable() {
    const {t} = usePolTranslation();
    const {
        isTableLoading,
        tableHeaders,
        tableRows,
        fullData,
        onSort
    } = useImportTable(IMPORT_MAPPING_DATA, IMPORT_MAPPING_DATA_SEARCH_FORM);
    const {
        methods,
        identifierFormMethods,
        form,
        isFormLoading,
        isSaveLoading,
        onSubmit,
        isConfirmOpen,
        columnNameToOverride,
        confirmAction,
        toggleConfirm,
    } = useMapImportForm(tableHeaders);

    return (
        <FormRender methods={methods} onSubmit={onSubmit} form={form} hasToHideRest>
            <div className="import-table__extras">
                <ImportRowsInfos fullData={fullData} identifierFormMethods={identifierFormMethods} form={form}/>

                <div className="import-table__extras__table-actions">
                    {form && (
                        <SubmitButton isLoading={isSaveLoading}>
                            {t('validate')}
                        </SubmitButton>
                    )}

                    <SearchContextPagination canChangeItemsPerPage={false}
                                             showWrapperEvenIfNoPages/>
                </div>
            </div>

            <div className="import-table__overflow">
                <Table className="import-table"
                       isLoading={isTableLoading || isFormLoading}
                       onSort={onSort}
                       theadData={handleTableHeaders(tableHeaders, methods, identifierFormMethods, form)}
                       tableRows={tableRows}
                       stickColumnsIndexes={[0]}
                       theadDataTrAfterRender={headerKey => (
                           <MapImportColumnFields headerKey={headerKey}/>
                       )}
                />
                <Confirm isOpen={isConfirmOpen}
                         confirmAction={confirmAction}
                         closeAction={toggleConfirm}
                         title={t('warning')}>
                    <div style={{maxWidth: '400px'}}>
                        <p dangerouslySetInnerHTML={{__html: t('map_import_confirm_override', {columnName: columnNameToOverride})}}/>
                    </div>
                </Confirm>
            </div>

            <div className="import-table__extras__table-actions">
                <SearchContextPagination canChangeItemsPerPage={false}
                                         showWrapperEvenIfNoPages/>

                {form && (
                    <SubmitButton>
                        {t('validate')}
                    </SubmitButton>
                )}
            </div>
        </FormRender>
    )
}

function handleTableHeaders(tableHeaders, methods, identifierFormMethods, form) {
    if (!form) return tableHeaders;

    const tableHeadersWithClassNames = tableHeaders.map(header => {
        const value = methods.watch(header.key) ?? form.results[header.key]?.value;

        return {
            ...header,
            // If the field is not mapped, add a class to highlight it
            className: !value ? 'table__header--error' : '',
        }
    });

    // Put the identifiers columns at the beginning of the table with a specific class name
    const identifierValues = identifierFormMethods.watch();
    const columnsToPlaceAtStart = Object.entries(identifierValues ?? {}).map(([identifierName, selectedValues]) => {
        const selectedColumns = Object.entries(methods.getValues())
            .filter(([, fieldValue]) => selectedValues.includes(fieldValue))
            .map(([fieldName]) => fieldName);

        return selectedColumns.map(() => {
            const headerIndex = tableHeadersWithClassNames.findIndex(header => selectedColumns.includes(header.key));

            const [header] = tableHeadersWithClassNames.splice(headerIndex, 1);
            return {
                ...header,
                className: `table__header--identifier ${identifierName} ${header.className}`.trim(),
                bodyClassName: 'text-semibold',
            };
        });
    }).flat()
    tableHeadersWithClassNames.unshift(...columnsToPlaceAtStart);

    return tableHeadersWithClassNames;
}
